import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import employeeDeploymentService from '../../../services/employeeDeploymentService';
import offerLettersService from '../../../services/offerLettersService';

const AddDeploymentModal = ({ isOpen, onClose, deployment, employees, clientSites, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState(null);
  const [deploymentType, setDeploymentType] = useState('existing'); // 'existing' or 'new_hire'
  const [offerLetters, setOfferLetters] = useState([]);

  const [formData, setFormData] = useState({
    employee_id: '',
    offer_letter_id: '',
    client_site_id: '',
    deployment_start_date: '',
    deployment_end_date: '',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    if (deployment) {
      setDeploymentType('existing'); // Editing is always existing employee

      // Format dates for date input (YYYY-MM-DD)
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };

      // Find employee UUID
      let employeeId = deployment.employee_id || deployment.employeeId || deployment.employee?.id || '';
      if (employeeId && employees && employees.length > 0) {
        const foundEmployee = employees.find(emp =>
          emp.id === employeeId || emp.employee_id === employeeId
        );
        if (foundEmployee) {
          employeeId = foundEmployee.id;
        }
      }

      setFormData({
        employee_id: employeeId,
        offer_letter_id: '',
        client_site_id: deployment.client_site_id || '',
        deployment_start_date: formatDate(deployment.deployment_start_date),
        deployment_end_date: formatDate(deployment.deployment_end_date),
        status: deployment.status || 'active',
        notes: deployment.notes || ''
      });
    } else {
      // Set default start date to today
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        employee_id: '',
        offer_letter_id: '',
        client_site_id: '',
        deployment_start_date: today,
        deployment_end_date: '',
        status: 'active',
        notes: ''
      });
      // Reset type when opening for new creation
      setDeploymentType('existing');
    }
    setError(null);
  }, [deployment, isOpen, employees]);

  // Fetch accepted offer letters when switching to new_hire mode
  useEffect(() => {
    if (deploymentType === 'new_hire' && isOpen) {
      fetchOfferLetters();
    }
  }, [deploymentType, isOpen]);

  const fetchOfferLetters = async () => {
    setFetchingData(true);
    try {
      const result = await offerLettersService.getAcceptedOfferLetters();
      if (result.success) {
        setOfferLetters(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching offer letters:', err);
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);

    // Auto-fill details from offer letter
    if (field === 'offer_letter_id' && value && offerLetters.length > 0) {
      const selectedOffer = offerLetters.find(ol => ol.id === value);
      if (selectedOffer) {
        // Auto-set start date from joining date if available
        if (selectedOffer.joining_date) {
          const joiningDate = new Date(selectedOffer.joining_date).toISOString().split('T')[0];
          setFormData(prev => ({ ...prev, deployment_start_date: joiningDate }));
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!deployment && deploymentType === 'existing' && !formData.employee_id) {
      setError('Employee is required');
      return;
    }

    if (deploymentType === 'new_hire' && !formData.offer_letter_id) {
      setError('Offer Letter is required');
      return;
    }

    if (!formData.client_site_id) {
      setError('Client site is required');
      return;
    }

    if (!formData.deployment_start_date) {
      setError('Start date is required');
      return;
    }

    // Validate dates
    if (formData.deployment_end_date && formData.deployment_end_date < formData.deployment_start_date) {
      setError('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        deployment_end_date: formData.deployment_end_date || null
      };

      // Clean up based on type
      if (deploymentType === 'existing') {
        delete submitData.offer_letter_id;
      } else {
        delete submitData.employee_id; // Backend will create employee
      }

      const result = deployment
        ? await employeeDeploymentService.updateDeployment(deployment.id, submitData)
        : await employeeDeploymentService.createDeployment(submitData);

      if (result?.success) {
        onSave?.();
      } else {
        setError(result?.error || `Failed to ${deployment ? 'update' : 'create'} deployment`);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Deployment save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">
            {deployment ? 'Edit Deployment' : 'Add New Deployment'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Deployment Type Toggle (Only for new deployments) */}
          {!deployment && (
            <div className="grid grid-cols-2 gap-4 mb-4 bg-muted/30 p-1 rounded-lg">
              <button
                type="button"
                className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${deploymentType === 'existing'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                onClick={() => setDeploymentType('existing')}
              >
                Existing Employee
              </button>
              <button
                type="button"
                className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${deploymentType === 'new_hire'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                onClick={() => setDeploymentType('new_hire')}
              >
                New Hire (Offer Letter)
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Existing Employee Select */}
            {deploymentType === 'existing' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Employee *
                </label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => handleInputChange('employee_id', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  required={deploymentType === 'existing'}
                  disabled={!!deployment}
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name} ({emp.employee_id})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* New Hire (Offer Letter) Select */}
            {deploymentType === 'new_hire' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Applicant (Accepted Offer Letter) *
                </label>
                {fetchingData ? (
                  <div className="text-sm text-muted-foreground">Loading offer letters...</div>
                ) : (
                  <select
                    value={formData.offer_letter_id}
                    onChange={(e) => handleInputChange('offer_letter_id', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                    required={deploymentType === 'new_hire'}
                  >
                    <option value="">Select Applicant</option>
                    {offerLetters.length > 0 ? (
                      offerLetters.map(ol => (
                        <option key={ol.id} value={ol.id}>
                          {ol.applicant_name || ol.employee_name} - {ol.position} (Joined: {new Date(ol.joining_date).toLocaleDateString()})
                        </option>
                      ))
                    ) : (
                      <option disabled>No accepted offer letters found</option>
                    )}
                  </select>
                )}
                {offerLetters.length === 0 && !fetchingData && (
                  <p className="text-xs text-amber-600 mt-1">
                    No accepted offer letters found. Please ensure offer letters are marked as "Accepted" in HR module.
                  </p>
                )}
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Client Site *
              </label>
              <select
                value={formData.client_site_id}
                onChange={(e) => handleInputChange('client_site_id', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                required
              >
                <option value="">Select Client Site</option>
                {clientSites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.site_name} - {site.company_name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Start Date *"
              type="date"
              value={formData.deployment_start_date}
              onChange={(e) => handleInputChange('deployment_start_date', e.target.value)}
              required
            />

            <Input
              label="End Date"
              type="date"
              value={formData.deployment_end_date}
              onChange={(e) => handleInputChange('deployment_end_date', e.target.value)}
              min={formData.deployment_start_date}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="transferred">Transferred</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows="3"
                placeholder="Additional notes about this deployment"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  {deployment ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                deployment ? 'Update Deployment' : 'Create Deployment'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeploymentModal;

